// One sink interface and its backends (decision 0064: "one sink interface with a Supabase backend and stub backends
// for PostHog (analytics) and Sentry (errors), left unconfigured"). A backend takes batches of records; which backends
// a game uses is configuration (`selectBackends`), and a backend with no keys reports `configured: false`, so the sink
// never hands it anything. The PostHog and Sentry backends are stubs: configured, they shape each record the way that
// service's API takes it and keep it for inspection, but never touch the network; going public means swapping a stub
// for the real client plus a key. Nothing here holds a key.
import { NotReadyError } from "./batcher.js";

/** A JSON value (records' structured fields). */
export type Json = string | number | boolean | null | Json[] | { [k: string]: Json };

/** What a record is: a gameplay event, an error report, a field performance sample. */
export type RecordKind = "event" | "error" | "sample";

/** One telemetry record, as every backend receives it. */
export interface TelemetryRecord {
  kind: RecordKind;
  /** The event's name; for an error its source ("uncaught", "rejection", "system"…); for a sample "field". */
  name: string;
  /** The session's random id (no account, no device fingerprint). */
  session: string;
  /** The record's number in its session, from 1. */
  seq: number;
  /** Milliseconds since the session started. */
  t: number;
  /** The game's build ("0.1.0+abc123"). */
  build: string;
  /** The record's fields: an event's kept primitives; an error report's or a sample's structured body. */
  data: { [k: string]: Json };
}

/** A telemetry backend. */
export interface TelemetryBackend {
  /** "supabase", "posthog", "sentry", "memory"… */
  readonly name: string;
  /** Has what it needs to send (keys, a client). An unconfigured backend is given nothing. */
  readonly configured: boolean;
  /** The kinds it takes. */
  readonly accepts: readonly RecordKind[];
  /** Send one batch. Rejects with a NotReadyError to wait (signed out), with anything else to be retried. */
  send(batch: TelemetryRecord[]): Promise<void>;
}

/** An in-memory backend for tests and the dev handle: keeps every batch it was sent. `fail` makes sends reject. */
export interface MemoryBackend extends TelemetryBackend {
  batches: TelemetryRecord[][];
  records(): TelemetryRecord[];
  /** Reject the next `n` sends ("wait": as not ready). */
  fail(n: number, how?: "error" | "wait"): void;
}

/** Make an in-memory backend. */
export function memoryBackend(o: { name?: string; accepts?: readonly RecordKind[] } = {}): MemoryBackend {
  let failing = 0;
  let how: "error" | "wait" = "error";
  const batches: TelemetryRecord[][] = [];
  return {
    name: o.name ?? "memory",
    configured: true,
    accepts: o.accepts ?? ["event", "error", "sample"],
    batches,
    records: () => batches.flat(),
    fail(n, h = "error") {
      failing = n;
      how = h;
    },
    async send(batch) {
      if (failing > 0) {
        failing--;
        throw how === "wait" ? new NotReadyError("memory backend: not ready") : new Error("memory backend: planned failure");
      }
      batches.push(batch.map((r) => structuredClone(r)));
    },
  };
}

// --- Supabase ---------------------------------------------------------------------------------------------------

/** The part of a supabase-js client the backend uses (so this package needs no supabase-js; `@rpg/save/supabase`'s
 *  connection's client fits). */
export interface SupabaseLike {
  schema(name: string): { from(table: string): { insert(rows: object[]): PromiseLike<{ error: { message?: string; code?: string } | null }> } };
  auth: { getSession(): PromiseLike<{ data: { session: { user: { id: string } } | null }; error: { message?: string } | null }> };
}

/** Options for {@link supabaseBackend}. */
export interface SupabaseTelemetryOptions {
  /** The client, or a getter (null while there is none: the batches wait). */
  client: SupabaseLike | (() => SupabaseLike | null);
  /** Postgres schema, default `rpg`. */
  schema?: string;
  /** Tables per kind, default `telemetry_events`, `telemetry_errors`, `telemetry_samples`. */
  tables?: Partial<Record<RecordKind, string>>;
}

/** The tables' default names (the migration's). */
export const SUPABASE_TABLES: Readonly<Record<RecordKind, string>> = { event: "telemetry_events", error: "telemetry_errors", sample: "telemetry_samples" };

/** A row as the tables hold it (one shape for all three; see the migration). */
export interface TelemetryRow {
  user_id: string;
  session_id: string;
  seq: number;
  t_ms: number;
  build: string;
  name: string;
  data: TelemetryRecord["data"];
}

/** The backend for the `rpg` schema's telemetry tables: inserts only, as the signed-in player (the tables' RLS lets a
 *  player insert and read their own rows and nothing else; anon gets nothing). Signed out, a batch waits. */
export function supabaseBackend(o: SupabaseTelemetryOptions): TelemetryBackend {
  const client = (): SupabaseLike | null => (typeof o.client === "function" ? o.client() : o.client);
  const schema = o.schema ?? "rpg";
  const tables = { ...SUPABASE_TABLES, ...o.tables };
  return {
    name: "supabase",
    configured: true,
    accepts: ["event", "error", "sample"],
    async send(batch) {
      const c = client();
      if (!c) throw new NotReadyError("supabase: no client");
      const { data, error } = await c.auth.getSession();
      if (error) throw new Error(`supabase session: ${error.message ?? "failed"}`);
      const uid = data.session?.user.id;
      if (!uid) throw new NotReadyError("supabase: not signed in");
      for (const kind of ["event", "error", "sample"] as const) {
        const rows: TelemetryRow[] = batch.filter((r) => r.kind === kind).map((r) => ({ user_id: uid, session_id: r.session, seq: r.seq, t_ms: Math.round(r.t), build: r.build, name: r.name, data: r.data }));
        if (!rows.length) continue;
        const res = await c.schema(schema).from(tables[kind]).insert(rows);
        if (res.error) throw new Error(`supabase insert ${tables[kind]}: ${res.error.message ?? "failed"}${res.error.code ? ` (${res.error.code})` : ""}`);
      }
    },
  };
}

// --- external stubs -----------------------------------------------------------------------------------------------

/** A stub of an external service: what it would have sent, never sent. */
export interface StubBackend extends TelemetryBackend {
  /** The payloads shaped for the service's API (configured only). */
  shaped(): unknown[];
}

/** A PostHog capture call's body (https://posthog.com/docs/api/capture). */
export interface PosthogCapture {
  api_key: string;
  event: string;
  distinct_id: string;
  timestamp_ms: number;
  properties: Record<string, Json>;
}

/** PostHog (analytics): a stub. Configured only with an `apiKey`; then it shapes events and samples as capture calls
 *  and keeps them; it never sends. */
export function posthogStub(o: { apiKey?: string | undefined; host?: string | undefined } = {}): StubBackend {
  const shaped: PosthogCapture[] = [];
  return {
    name: "posthog",
    configured: !!o.apiKey,
    accepts: ["event", "sample"],
    shaped: () => [...shaped],
    async send(batch) {
      for (const r of batch) shaped.push({ api_key: o.apiKey ?? "", event: r.kind === "sample" ? "$field_sample" : r.name, distinct_id: r.session, timestamp_ms: r.t, properties: { ...r.data, $session_id: r.session, build: r.build, seq: r.seq } });
    },
  };
}

/** A Sentry event's body, as an envelope's item carries it (https://develop.sentry.dev/sdk/data-model/event-payloads/). */
export interface SentryEvent {
  event_id: string;
  level: "error";
  platform: "javascript";
  release: string;
  logger: string;
  message: string;
  exception: { values: { type: string; value: string; stacktrace?: { frames: { function: string }[] } }[] };
  contexts: Record<string, Json>;
  breadcrumbs: { values: Json[] };
}

/** Sentry (errors): a stub. Configured only with a `dsn`; then it shapes error reports as Sentry events and keeps
 *  them; it never sends. */
export function sentryStub(o: { dsn?: string | undefined } = {}): StubBackend {
  const shaped: SentryEvent[] = [];
  return {
    name: "sentry",
    configured: !!o.dsn,
    accepts: ["error"],
    shaped: () => [...shaped],
    async send(batch) {
      for (const r of batch) {
        const d = r.data;
        const stack = typeof d.stack === "string" ? d.stack.split("\n").map((l) => ({ function: l.trim() })) : null;
        shaped.push({
          event_id: `${r.session.replace(/-/g, "").slice(0, 24)}${String(r.seq).padStart(8, "0")}`,
          level: "error",
          platform: "javascript",
          release: r.build,
          logger: r.name,
          message: String(d.message ?? ""),
          exception: { values: [{ type: r.name, value: String(d.message ?? ""), ...(stack ? { stacktrace: { frames: stack } } : {}) }] },
          contexts: { device: d.device ?? null },
          breadcrumbs: { values: Array.isArray(d.recent) ? d.recent : [] },
        });
      }
    },
  };
}

/** Pick backends by name from the factories a game offers (configuration chooses; unknown names are reported). */
export function selectBackends(names: readonly string[], factories: Readonly<Record<string, () => TelemetryBackend>>): { backends: TelemetryBackend[]; unknown: string[] } {
  const backends: TelemetryBackend[] = [];
  const unknown: string[] = [];
  for (const n of names) {
    const make = factories[n];
    if (make) backends.push(make());
    else unknown.push(n);
  }
  return { backends, unknown };
}
